import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Smartphone, Plus, Trash2, Check, Loader2, Lock, ExternalLink, RefreshCw, CalendarClock } from "lucide-react";
import { api, money, errorMessage } from "../api";
import "./digest.css";
import "./insights.css";
import WeeklyView from "./Weekly";
import WhatsAppConnect from "./WhatsAppConnect";
import { fmtDT } from "../lib/units";
import { DateInput } from "../components/DateInput";

export const ddmmyyyy = (iso) => (iso ? iso.split("-").reverse().join("-") : "");
const hourLabel = (h) => `${((h + 11) % 12) + 1} ${h < 12 ? "AM" : "PM"}`;
const mins = (m) => (m == null ? "—" : `${Math.round(m)} min`);
const yesterdayIST = () => new Date(Date.now() + 330 * 60000 - 86400000).toISOString().slice(0, 10);

export function digestText(d, origin) {
  const m = d.metrics, t = m.totals;
  const lines = [
    `*FynDine Daily Digest*`,
    `${d.branch_name}${d.branch_city ? ", " + d.branch_city : ""} · ${ddmmyyyy(d.date)}`,
    ``,
    `Sales: ${money(t.sales)} (${t.bills} bills, avg ${money(t.avg_bill)})`,
    `Top waiter: ${m.top_waiter ? `${m.top_waiter.name} – ${money(m.top_waiter.sales)}` : "—"}`,
    `Peak hour: ${m.peak_hour ? `${hourLabel(m.peak_hour.hour)}–${hourLabel((m.peak_hour.hour + 1) % 24)} (${money(m.peak_hour.sales)})` : "—"}`,
    `Best-seller: ${m.best_seller ? `${m.best_seller.name} (${m.best_seller.qty} sold)` : "—"}`,
    `Fastest table: ${m.fastest_table ? `${m.fastest_table.table} (${mins(m.fastest_table.service_mins ?? m.fastest_table.turnover_mins)})` : "—"}`,
    m.cancellations?.count ? `Cancellations/reductions: ${m.cancellations.count}` : null,
    ``,
    `Full report: ${origin}/digest/${d.id}`,
  ];
  return lines.filter((l) => l !== null).join("\n");
}

function Settings({ cfg, onSaved }) {
  const [f, setF] = useState(cfg);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => setF(cfg), [cfg]);
  const setR = (i, k, v) => setF((x) => ({ ...x, recipients: x.recipients.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try { const { data } = await api.put("/digest/settings", f); onSaved(data); setMsg("Saved"); }
    catch (e2) { setMsg(errorMessage(e2)); } finally { setBusy(false); }
  };
  return (
    <form className="panel dg-settings" onSubmit={save} data-testid="digest-settings">
      <div className="panel-head"><div><span className="overline"><CalendarClock size={11} /> SCHEDULE</span><h2>Digest settings</h2></div></div>
      <label className="dg-switch"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid="digest-enabled" /> Prepare a digest every morning</label>
      <label className="dg-switch"><input type="checkbox" checked={f.auto_whatsapp} onChange={(e) => setF({ ...f, auto_whatsapp: e.target.checked })} data-testid="digest-auto-whatsapp" /> Auto-send on WhatsApp (no tap)</label>
      <label className="dg-switch"><input type="checkbox" checked={f.weekly_enabled} onChange={(e) => setF({ ...f, weekly_enabled: e.target.checked })} data-testid="digest-weekly-enabled" /> Weekly report every Monday</label>
      <label className="dg-field">Ready at (IST) — covers the previous day<input type="time" value={f.send_time} onChange={(e) => setF({ ...f, send_time: e.target.value })} data-testid="digest-time" /></label>
      <span className="overline" style={{ margin: "14px 0 8px" }}>RECIPIENTS · OWNER + UP TO 3</span>
      {f.recipients.map((r, i) => (
        <div className="dg-recip" key={i}>
          <input value={r.name} placeholder={i === 0 ? "Owner" : "Name"} onChange={(e) => setR(i, "name", e.target.value)} data-testid={`digest-recip-name-${i}`} />
          <input value={r.phone} placeholder="Enter 10-digit mobile number" inputMode="numeric" onChange={(e) => setR(i, "phone", e.target.value)} data-testid={`digest-recip-phone-${i}`} />
          <button type="button" className="icon-button" onClick={() => setF({ ...f, recipients: f.recipients.filter((_, j) => j !== i) })} data-testid={`digest-recip-remove-${i}`}><Trash2 size={13} /></button>
        </div>
      ))}
      {f.recipients.length < 4 && <button type="button" className="secondary-button" onClick={() => setF({ ...f, recipients: [...f.recipients, { name: "", phone: "" }] })} data-testid="digest-recip-add"><Plus size={13} /> Add number</button>}
      <button className="primary-button full" disabled={busy} data-testid="digest-settings-save">{busy ? <Loader2 className="spin" size={14} /> : <Check size={14} />} Save settings</button>
      {msg && <small className="dg-msg" data-testid="digest-settings-msg">{msg}</small>}
    </form>
  );
}

function Deliveries({ list }) {
  if (!list?.length) return null;
  return (
    <div className="dg-deliv" data-testid="delivery-log">
      <span className="overline">AUTO WHATSAPP DELIVERIES</span>
      {list.slice(-6).reverse().map((x, i) => (
        <p key={i} data-testid={`delivery-${i}`}><em className={`pill ${x.status === "failed" ? "mod-cancel" : x.status === "mock_sent" ? "mod-note" : "success"}`}>{x.status === "mock_sent" ? "MOCK sent" : x.status}</em> {x.name || ""} {x.phone} <span>{fmtDT(x.at)}</span>{x.error && <small>{x.error}</small>}</p>
      ))}
    </div>
  );
}

export function Preview({ d, recipients, onSent, text: givenText, kind = "daily", title }) {
  const text = givenText || digestText(d, window.location.origin);
  const base = kind === "daily" ? "digests" : "weekly-reports";
  const sentTo = new Set((d.sent_log || []).map((s) => s.phone));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const log = (phone, channel) => api.post(`/${base}/${d.id}/sent`, { phone, channel }).then(onSent).catch(() => {});
  const auto = async () => {
    setBusy(true); setMsg("");
    try { const { data } = await api.post(`/${base}/${d.id}/auto-send`); setMsg(`${data.deliveries.length} message(s) processed`); onSent(); }
    catch (e) { setMsg(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="panel dg-preview" data-testid={`${kind}-preview`}>
      <div className="panel-head"><div><span className="overline">{title}</span><h2>{d.branch_name}</h2></div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="secondary-button" onClick={auto} disabled={busy} data-testid={`${kind}-auto-send`}>{busy ? <Loader2 className="spin" size={13} /> : <MessageCircle size={13} />} Auto-send now</button>
          <a className="secondary-button" href={`/${kind === "daily" ? "digest" : "weekly"}/${d.id}`} target="_blank" rel="noreferrer" data-testid={`${kind}-open-report`}><ExternalLink size={13} /> Full report</a>
        </div></div>
      {msg && <small className="dg-msg" data-testid={`${kind}-auto-msg`}>{msg}</small>}
      <pre className="dg-bubble" data-testid={`${kind}-message`}>{text}</pre>
      <Deliveries list={d.deliveries} />
      <span className="overline" style={{ margin: "16px 0 8px" }}>MANUAL SEND · ONE TAP PER NUMBER</span>
      {!recipients.length && <div className="empty small">Add recipients in settings.</div>}
      {recipients.map((r, i) => {
        const p = r.phone.replace(/\D/g, "");
        const enc = encodeURIComponent(text);
        return (
          <div className="dg-send" key={r.phone} data-testid={`${kind}-send-row-${i}`}>
            <div><b>{r.name || "Recipient"}</b><small>{r.phone}{sentTo.has(r.phone) && <em className="pill success" data-testid={`${kind}-sent-badge-${i}`}>sent</em>}</small></div>
            <a className="share-button whatsapp" href={`https://wa.me/${p}?text=${enc}`} target="_blank" rel="noreferrer" onClick={() => log(r.phone, "whatsapp")} data-testid={`${kind}-send-whatsapp-${i}`}><MessageCircle size={14} /> WhatsApp</a>
            <a className="share-button sms" href={`sms:+${p}?body=${enc}`} onClick={() => log(r.phone, "sms")} data-testid={`${kind}-send-sms-${i}`}><Smartphone size={14} /> SMS</a>
          </div>
        );
      })}
    </div>
  );
}

export default function DigestView(props) {
  const [tab, setTab] = useState("daily");
  if (!props.allowed) return <DailyDigest {...props} />;
  return (
    <>
      <div className="ins-toggle dg-tabs">
        {[["daily", "Daily digest"], ["weekly", "Weekly report"], ["whatsapp", "WhatsApp connection"]].map(([id, l]) => (
          <button key={id} className={tab === id ? "on" : ""} onClick={() => setTab(id)} data-testid={`digest-tab-${id}`}>{l}</button>
        ))}
      </div>
      {tab === "daily" && <DailyDigest {...props} />}
      {tab === "weekly" && <WeeklyView branch={props.branch} />}
      {tab === "whatsapp" && <WhatsAppConnect branch={props.branch} />}
    </>
  );
}

function DailyDigest({ branch, allowed, onUpgrade }) {
  const [cfg, setCfg] = useState(null);
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [day, setDay] = useState(yesterdayIST());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    if (!allowed) return;
    try {
      const [c, l] = await Promise.all([api.get("/digest/settings"), api.get(`/digests?branch=${branch}`)]);
      setCfg(c.data); setList(l.data); setSel((s) => l.data.find((x) => x.id === s?.id) || l.data[0] || null); setErr("");
    } catch (e) { setErr(errorMessage(e)); }
  }, [branch, allowed]);
  useEffect(() => { load(); }, [load]);
  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/digests/generate?branch=${branch}&date=${day}`); await load(); setSel(data); }
    catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
  };
  if (!allowed) {
    return (
      <div className="panel kds-locked" data-testid="digest-locked"><Lock size={22} /><h2>Daily Owner Digest is a Professional feature</h2>
        <p>Every morning get yesterday's sales, top waiter, peak hour and best-seller ready to send on WhatsApp.</p>
        <button className="primary-button" onClick={onUpgrade} data-testid="digest-upgrade-btn">See plans</button></div>
    );
  }
  return (
    <div className="dg-layout" data-testid="digest-page">
      <div>
        {cfg && <Settings cfg={cfg} onSaved={setCfg} />}
        <div className="panel dg-list" data-testid="digest-list">
          <div className="panel-head"><div><span className="overline">HISTORY</span><h2>Past digests</h2></div><button className="icon-button" onClick={load} data-testid="digest-refresh"><RefreshCw size={14} /></button></div>
          <div className="dg-gen">
            <DateInput lang="en-GB" value={day} onChange={(e) => setDay(e.target.value)} data-testid="digest-generate-date" />
            <button className="secondary-button" onClick={generate} disabled={busy || !day} data-testid="digest-generate-button">{busy ? <Loader2 className="spin" size={13} /> : <RefreshCw size={13} />} Build now</button>
          </div>
          {!list.length && <div className="empty small">No digests yet. The first one is prepared after your send time.</div>}
          {list.map((d) => (
            <button key={d.id} className={`dg-row ${sel?.id === d.id ? "on" : ""}`} onClick={() => setSel(d)} data-testid={`digest-row-${d.date}`}>
              <b>{ddmmyyyy(d.date)}</b><span>{money(d.metrics.totals.sales)} · {d.metrics.totals.bills} bills</span>
              <em className={d.sent_log?.length ? "pill success" : "pill accent"}>{d.sent_log?.length ? `sent ×${d.sent_log.length}` : "ready"}</em>
            </button>
          ))}
        </div>
      </div>
      <div>
        {err && <div className="auth-alert error" data-testid="digest-error">{err}</div>}
        {sel ? <Preview d={sel} recipients={cfg?.recipients || []} onSent={load} kind="daily" title={`DIGEST · ${ddmmyyyy(sel.date)}`} /> : <div className="panel empty">Select or build a digest to preview it.</div>}
      </div>
    </div>
  );
}
